import { useEffect, useRef, useState } from 'react';
import mapboxgl from 'mapbox-gl';
import { createRoot } from 'react-dom/client';
import type {
  VesselPosition,
  BaseVesselData,
  StaticVesselData,
  VesselFilter,
  FavoritesMap,
} from '../types/vessel';
import { getVesselName, getFlagCode, hasValidCoordinates } from '../utils/vessel';
import { VesselPopup } from './VesselPopup';

const MARKER_ICON =
  'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAA8AAAAeCAYAAADzXER0AAAAAXNSR0IArs4c6QAAAARnQU1BAACxjwv8YQUAAAAJcEhZcwAAEnQAABJ0Ad5mH3gAAANVSURBVEhLzZTPbxNHFMddiVQttIVjT0UgQVUIh6RBhthKDCYkMXjteDdx1vZ6f3h/Oy4YhYiaECmAkpKGA2lUQVIpCEK4wQFREQX1wh/QyCAhflRqji1wgqri4i8zZkJijFukXvqVnqwdv8+8efPeG8//T8LQUJ038a2+/5tzjWzp/dXYqdTv8Hc+CZqnb18APmPL76eGNmGM53l0JOwXXWd/jrLlf1e9cGhr64Ge37OWjh4xiYB+8tblUulT9ndtAfA0hNLfx1Mycn0u9IyKFi75Z3NubDNzqa3O0Zv1Ad5YymgabMtC1nXA8b3YKRwqMJd3i0T9oP3o9BgnKrDNDEwCO7YFRVHRKh5+ZFxdqh3duVLc0CoNLEiyCscyYZomLPJrmwbahPTfzcppjrlWS7v8ayjQbTxXUiIcx2GwBdcyEBFEtB+ZnBm6hzrmXqmmWD7GiRosI1OGKEyNnkIzLHhDiWtf5HIfMvcV3QPqfOnCXDQmoK8vi4yuQ5Ikkq9SPoWmpODjUkvdk3d8DFlRcn5xbVA99iBFAHpJNOLIyAj6+/uhk40MTUZEziKY/0FkyGp9/nGTb8+iTUCNlGliYgJTU1OYmZnB4XyebGgjHIlhw8avuhmwosjovNwS4v+yTR2ZTAazs7PIE2h6ehqDg4Ogm0a7YvBJx26Ml0ofMey1Dh6fG46k3fJlUZgel24wPj5e/qappKQ09hknHqr3S58w7LX8yYGCkCJOrL706LIsQ1VVGIZBItugXdcSlYvr1TOV8I5ArNCdlMu5LZdotS2XblfwYNHj2VoJb2nwF3oSElynNkyje/17CeyphDdt9xbiiRRcMgj/DAeq4S93thCYRq4NU/MG9hN4fSX8dUgsxJOkm2rkTKPSZvGH4kXP7qOVcKs2fJyX6ARVg9Roi6bJ7e9LHrkbPvOkEo6cun4qnM6W6/wuOOu66In3ot397tG5p289SdzJG+HmtugzWuflsqy2XC4HjouiKZa7eLNUqpqsNc1tXYsGebMcMr/lsXxjOrKmhjAvYmNjR/VLOvkH1h3o//G3vbyGzl4L7XHzjXWIFoKRBDrMYeiXigmGrOg8sEafe2wJZ38Z5scWBt+22OitE8pPxaGBOy+3MeS/yON5Bb5UGV0oP1oM';

interface MapProps {
  mapboxToken: string;
  vessels: Record<string, VesselPosition>;
  baseData: Record<string, BaseVesselData>;
  staticData: Record<string, StaticVesselData>;
  favorites: FavoritesMap;
  hiddenGroups: Record<string, boolean>;
  currentFilter: VesselFilter;
  searchMatches: string[] | null;
  labelsVisible: boolean;
  unlocodeRegister: Array<{ Country: string; Location: string; Name: string }>;
  countries: Array<{ CountryCode: string; CountryName: string }>;
  flyToVesselId?: string | null;
  sidebarCollapsed: boolean;
  onToggleFavorite?: (id: string) => void;
}

export function Map({
  mapboxToken,
  vessels,
  baseData,
  staticData,
  favorites,
  hiddenGroups,
  currentFilter,
  searchMatches,
  labelsVisible,
  unlocodeRegister,
  countries,
  flyToVesselId,
  sidebarCollapsed,
  onToggleFavorite,
}: MapProps) {
  const mapContainer = useRef<HTMLDivElement>(null);
  const mapRef = useRef<mapboxgl.Map | null>(null);
  const markersRef = useRef<Record<string, mapboxgl.Marker>>({});
  const popupRootsRef = useRef<Record<string, ReturnType<typeof createRoot>>>({});
  const [mapLoaded, setMapLoaded] = useState(false);

  useEffect(() => {
    if (!mapContainer.current || mapRef.current) return;

    mapboxgl.accessToken = mapboxToken;
    const map = new mapboxgl.Map({
      container: mapContainer.current,
      style: 'mapbox://styles/mapbox/streets-v11',
      center: [0, 20],
      zoom: 2,
    });

    map.on('load', () => setMapLoaded(true));

    const handleResize = () => map.resize();
    window.addEventListener('resize', handleResize);

    const resizeObserver = new ResizeObserver(() => map.resize());
    if (mapContainer.current) resizeObserver.observe(mapContainer.current);

    mapRef.current = map;

    return () => {
      window.removeEventListener('resize', handleResize);
      resizeObserver.disconnect();
      map.remove();
      mapRef.current = null;
      setMapLoaded(false);
    };
  }, [mapboxToken]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapLoaded) return;

    const existingIds = new Set(Object.keys(markersRef.current));
    const newIds = new Set(Object.keys(vessels));

    existingIds.forEach((id) => {
      if (!newIds.has(id)) {
        markersRef.current[id].remove();
        delete markersRef.current[id];
        if (popupRootsRef.current[id]) {
          popupRootsRef.current[id].unmount();
          delete popupRootsRef.current[id];
        }
      }
    });

    Object.entries(vessels).forEach(([id, vessel]) => {
      if (vessel.Lat == null || vessel.Lon == null || vessel.Lat === 91 || vessel.Lat < -90 || vessel.Lat > 90) return;

      const base = baseData[id];
      const stat = staticData[id];
      const vesselName = getVesselName(vessel, id, base, stat);
      const flagCode = getFlagCode(stat?.FlagName, countries);

      let visible = true;
      if (currentFilter === 'Favorites' && !favorites[id]) visible = false;
      else if (currentFilter === 'LNG' && base?.Type !== 'LNG') visible = false;
      else if (currentFilter === 'Bunkervessel' && base?.Type !== 'Bunkervessel') visible = false;
      if (searchMatches && !searchMatches.includes(id)) visible = false;
      if (favorites[id] && hiddenGroups[favorites[id].group]) visible = false;

      if (markersRef.current[id]) {
        markersRef.current[id].setLngLat([vessel.Lon, vessel.Lat]);
        const el = markersRef.current[id].getElement();
        el.style.display = visible ? 'block' : 'none';

        const label = el.querySelector('.vessel-label') as HTMLElement;
        if (label) {
          label.textContent = vesselName;
          label.style.display = labelsVisible ? 'block' : 'none';
        }

        const img = el.querySelector('img') as HTMLImageElement;
        if (img) img.style.transform = `rotate(${vessel.Heading || 0}deg)`;

        if (popupRootsRef.current[id]) {
          popupRootsRef.current[id].render(
            <VesselPopup
              vessel={vessel}
              vesselId={id}
              baseData={base}
              staticData={stat}
              unlocodeRegister={unlocodeRegister}
              countries={countries}
              flagCode={flagCode}
              isFavorite={!!favorites[id]}
              onToggleFavorite={onToggleFavorite ? () => onToggleFavorite(id) : undefined}
            />
          );
        }
      } else {
        const el = document.createElement('div');
        el.style.cursor = 'pointer';

        const img = document.createElement('img');
        img.src = MARKER_ICON;
        img.style.width = '10px';
        img.style.height = '20px';
        img.style.transform = `rotate(${vessel.Heading || 0}deg)`;

        const label = document.createElement('div');
        label.className = 'vessel-label';
        label.textContent = vesselName;
        label.style.display = labelsVisible ? 'block' : 'none';

        el.appendChild(img);
        el.appendChild(label);
        el.style.display = visible ? 'block' : 'none';

        const popupEl = document.createElement('div');
        const popup = new mapboxgl.Popup({ offset: 25, closeButton: false }).setDOMContent(popupEl);

        const marker = new mapboxgl.Marker({ element: el, anchor: 'center' })
          .setLngLat([vessel.Lon, vessel.Lat])
          .setPopup(popup)
          .addTo(map);

        const root = createRoot(popupEl);
        popupRootsRef.current[id] = root;
        root.render(
          <VesselPopup
            vessel={vessel}
            vesselId={id}
            baseData={base}
            staticData={stat}
            unlocodeRegister={unlocodeRegister}
            countries={countries}
            flagCode={flagCode}
            isFavorite={!!favorites[id]}
            onToggleFavorite={onToggleFavorite ? () => onToggleFavorite(id) : undefined}
          />
        );

        markersRef.current[id] = marker;
      }
    });
  }, [mapLoaded, vessels, baseData, staticData, favorites, hiddenGroups, currentFilter, searchMatches, labelsVisible, unlocodeRegister, countries, onToggleFavorite]);

  useEffect(() => {
    if (!flyToVesselId || !mapRef.current || !vessels[flyToVesselId]) return;
    const vessel = vessels[flyToVesselId];

    if (!hasValidCoordinates(vessel)) {
      return;
    }

    mapRef.current.flyTo({ center: [vessel.Lon, vessel.Lat], zoom: 7 });
    setTimeout(() => markersRef.current[flyToVesselId]?.togglePopup(), 1000);
  }, [flyToVesselId, vessels]);

  useEffect(() => {
    if (mapRef.current) {
      const timer = setTimeout(() => mapRef.current?.resize(), 320);
      return () => clearTimeout(timer);
    }
  }, [sidebarCollapsed]);

  return (
    <div
      ref={mapContainer}
      style={{
        position: 'absolute',
        top: 0,
        bottom: 0,
        right: 0,
        left: sidebarCollapsed ? 0 : '220px',
        transition: 'left 0.3s ease',
      }}
    />
  );
}
